/**
 * @fileoverview User-related React Query hooks
 * 
 * These hooks provide declarative data fetching for user profile,
 * authentication, and user settings with automatic caching.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { userService, authService } from '../../services'
import { queryKeys } from '../../lib/queryClient'
import type { 
  UserProfile,
  LoginRequest,
  SignupRequest,
  TierUsageResponse
} from '../../services'

/**
 * Hook to get current user profile
 */
export const useUserProfile = (enabled: boolean = true) => {
  return useQuery({
    queryKey: queryKeys.user.profile,
    queryFn: () => userService.getProfile(),
    enabled,
    staleTime: 5 * 60 * 1000, // Cache user profile for 5 minutes
    retry: (failureCount, error: unknown) => {
      // Don't retry on 401 (unauthorized) - user needs to login
      if (error && typeof error === 'object' && 'status' in error && error.status === 401) return false
      return failureCount < 2
    },
  })
}

/**
 * Hook to get user tier and usage information
 */
export const useUserLimits = (enabled: boolean = true) => {
  return useQuery({
    queryKey: queryKeys.user.limits,
    queryFn: () => userService.getTierUsage(),
    enabled,
    staleTime: 2 * 60 * 1000, // Refresh usage info more frequently
  })
}

/**
 * Hook for user login
 */
export const useLogin = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (credentials: LoginRequest) => authService.login(credentials),
    
    onSuccess: (data) => {
      // Set user profile in cache immediately
      if (data.user) {
        queryClient.setQueryData(queryKeys.user.profile, data.user)
        queryClient.setQueryData(queryKeys.auth.user, data.user)
      }
      
      // Invalidate and refetch user-related data
      queryClient.invalidateQueries({ queryKey: queryKeys.user.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.user })
    },
  })
}

/**
 * Hook for user signup
 */
export const useSignup = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userData: SignupRequest) => authService.signup(userData),
    
    onSuccess: (data) => {
      // Set user profile in cache after successful signup
      if (data.user) {
        queryClient.setQueryData(queryKeys.user.profile, data.user)
        queryClient.setQueryData(queryKeys.auth.user, data.user)
      }
      
      // Invalidate user-related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.user.all })
    },
  })
}

/**
 * Hook for user logout
 */
export const useLogout = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => authService.logout(),
    
    onSuccess: () => {
      // Clear all cached data on logout
      queryClient.clear()
      
      // Could also selectively clear just user-related data:
      // queryClient.removeQueries({ queryKey: queryKeys.user.all })
      // queryClient.removeQueries({ queryKey: queryKeys.auth.user })
      // queryClient.removeQueries({ queryKey: queryKeys.strategies.lists() })
      // queryClient.removeQueries({ queryKey: queryKeys.backtests.lists() })
    },
  })
}

/**
 * Hook to update user profile
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (profileData: Partial<UserProfile>) => 
      userService.updateProfile(profileData),
    
    onMutate: async (newProfile) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.user.profile })
      
      const previousProfile = queryClient.getQueryData(queryKeys.user.profile)
      
      // Optimistically update the profile
      queryClient.setQueryData(queryKeys.user.profile, (old: UserProfile | undefined) => {
        if (!old) return old
        return { ...old, ...newProfile }
      })

      return { previousProfile }
    },

    onError: (_err, _newProfile, context) => {
      if (context?.previousProfile) {
        queryClient.setQueryData(queryKeys.user.profile, context.previousProfile)
      }
    },

    onSuccess: (data) => {
      // Update with real data from server
      queryClient.setQueryData(queryKeys.user.profile, data)
      queryClient.setQueryData(queryKeys.auth.user, data)
    },
  })
}

/**
 * Hook to check tier permissions
 */
export const useCheckTierPermission = () => {
  return useMutation({
    mutationFn: (action: 'create_strategy' | 'save_backtest' | 'share_strategy') => 
      userService.checkTierPermission(action),
  })
}

/**
 * Hook to refresh user data manually
 */
export const useRefreshUserData = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      // Invalidate all user-related queries to force refetch
      await queryClient.invalidateQueries({ queryKey: queryKeys.user.all })
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.user })
      
      // Wait for the profile to refetch
      return queryClient.fetchQuery({
        queryKey: queryKeys.user.profile,
        queryFn: () => userService.getProfile(),
      })
    },
  })
}
